import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Coins, Sparkles, Check, Loader2, ArrowRight, Gift } from 'lucide-react';
import { useCoins } from '../context/CoinContext';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export function CoinPurchaseModal({ open, onOpenChange, getAuthHeaders }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const { wallet, refreshWallet } = useCoins();

  useEffect(() => {
    if (open) {
      fetchPackages();
    }
  }, [open]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/coins/packages`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages || []);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load coin packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pkg) => {
    setSelectedPackage(pkg.package_id);
    setPurchasing(true);
    
    try {
      const response = await fetch(`${API}/coins/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          package_id: pkg.package_id,
          origin_url: window.location.origin
        })
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        // Redirect to Stripe checkout
        window.location.href = result.data.checkout_url;
      } else {
        toast.error(result.error || 'Failed to start purchase');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to connect to payment system');
    } finally {
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-background border-border" data-testid="coin-purchase-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Coins className="w-6 h-6 text-amber-400" />
            Get Coins
          </DialogTitle>
          <DialogDescription>
            Purchase coins to send gifts during live streams and support your favorite creators.
          </DialogDescription>
        </DialogHeader>

        {/* Current Balance */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <span className="text-sm text-muted-foreground">Your Balance</span>
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-400" />
            <span className="text-lg font-bold text-amber-400">{wallet.coin_balance.toLocaleString()}</span>
          </div>
        </div>

        {/* Packages Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto p-1">
            {packages.map((pkg) => (
              <div
                key={pkg.package_id}
                className={cn(
                  "relative rounded-lg border p-4 cursor-pointer transition-all hover:border-primary/50",
                  pkg.popular 
                    ? "border-secondary/50 bg-secondary/5" 
                    : "border-border bg-muted/20",
                  selectedPackage === pkg.package_id && "ring-2 ring-primary"
                )}
                onClick={() => !purchasing && handlePurchase(pkg)}
                data-testid={`coin-package-${pkg.package_id}`}
              >
                {pkg.popular && (
                  <Badge 
                    className="absolute -top-2 left-1/2 -translate-x-1/2 bg-secondary text-foreground text-xs"
                  >
                    Popular
                  </Badge>
                )}
                
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <Coins className="w-5 h-5 text-amber-400" />
                    <span className="text-xl font-bold">{pkg.coins.toLocaleString()}</span>
                  </div>
                  
                  {pkg.bonus_coins > 0 && (
                    <div className="flex items-center justify-center gap-1 text-green-400 text-sm mb-2">
                      <Sparkles className="w-3 h-3" />
                      <span>+{pkg.bonus_coins.toLocaleString()} bonus</span>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground mb-3">{pkg.name}</p>
                  
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={purchasing}
                    variant={pkg.popular ? "default" : "outline"}
                  >
                    {purchasing && selectedPackage === pkg.package_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      formatPrice(pkg.price_usd)
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/30 text-xs text-muted-foreground">
          <Gift className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            Coins are used to send virtual gifts during live streams. Creators receive a portion of the gift value as earnings.
            Coins are non-refundable once purchased.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
